/**
 * WC-07: Challenge Day Page
 *
 * Day detail page where players view enemy info and select creatures.
 * Loads actual creatures from the user's creature library.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  useChallengeStore,
  formatModeName,
  getModeIcon,
} from '../stores/useChallengeStore';
import { getCreatureLibraryService } from '../services/creatureLibraryService';
import { useAuth } from '../contexts/AuthContext';
import type { SerializedCreatureDocument } from '@drawn-of-war/shared/types/creature-storage';
import { PageAtmosphere } from '../components/atmosphere';
import '../styles/atmosphere/pages/challenge.css';
import './ChallengeDayPage.css';

export const ChallengeDayPage: React.FC = () => {
  const { day } = useParams<{ day: string }>();
  const navigate = useNavigate();
  const dayNum = parseInt(day || '1', 10);
  const { user, loading: authLoading } = useAuth();

  const {
    currentWeek,
    progress,
    selectedCreatures,
    isStartingChallenge,
    isLoading: isLoadingWeek,
    error,
    toggleCreature,
    startChallenge,
    clearError,
    fetchCurrentWeek,
  } = useChallengeStore();

  // Real creatures from library
  const [playerCreatures, setPlayerCreatures] = useState<SerializedCreatureDocument[]>([]);
  const [creaturesLoading, setCreaturesLoading] = useState(true);
  const [creaturesError, setCreaturesError] = useState<string | null>(null);

  // Fetch player's creatures from library - wait for auth to be ready
  useEffect(() => {
    // Wait for auth to be fully initialized before loading creatures
    // We need both: authLoading=false AND user to exist (so __getAuthToken is set)
    if (authLoading || !user) {
      return;
    }

    const fetchCreatures = async () => {
      setCreaturesLoading(true);
      setCreaturesError(null);
      try {
        // Small delay to ensure __getAuthToken is set by AuthContext's useEffect
        await new Promise(resolve => setTimeout(resolve, 100));

        const libraryService = getCreatureLibraryService();
        const response = await libraryService.listCreatures({ limit: 50 });
        setPlayerCreatures(response.creatures);
      } catch (err) {
        console.error('Failed to load creatures:', err);
        setCreaturesError(err instanceof Error ? err.message : 'Failed to load creatures');
      } finally {
        setCreaturesLoading(false);
      }
    };

    fetchCreatures();
  }, [authLoading, user]);

  // Track if we've tried to fetch the week data
  const [hasFetchedWeek, setHasFetchedWeek] = useState(false);

  // Fetch week data if not present
  useEffect(() => {
    if (!currentWeek && !isLoadingWeek && !hasFetchedWeek) {
      setHasFetchedWeek(true);
      fetchCurrentWeek();
    }
  }, [currentWeek, isLoadingWeek, hasFetchedWeek, fetchCurrentWeek]);

  // Get day data
  const dayData = currentWeek?.days?.find((d) => d.day === dayNum);
  const dayProgress = progress?.days[dayNum];

  // Redirect back to hub only after we've tried to fetch and still no data
  useEffect(() => {
    if (hasFetchedWeek && !isLoadingWeek && !currentWeek) {
      navigate('/challenges');
    }
  }, [hasFetchedWeek, isLoadingWeek, currentWeek, navigate]);

  const handleStartBattle = useCallback(async () => {
    if (selectedCreatures.length < 3) {
      alert('Select at least 3 creatures to continue');
      return;
    }

    try {
      // Start the challenge attempt via API
      const attemptId = await startChallenge(dayNum, selectedCreatures);

      // Navigate to deployment page with challenge parameters
      // The deployment page will handle PvE challenge setup
      const params = new URLSearchParams({
        mode: 'challenge',
        weekId: currentWeek?.weekId || '',
        day: dayNum.toString(),
        attemptId: attemptId || '',
        creatures: selectedCreatures.join(','),
      });

      navigate(`/deployment?${params.toString()}`);
    } catch (err) {
      // Error is handled in store
      console.error('Failed to start challenge:', err);
    }
  }, [selectedCreatures, dayNum, currentWeek, startChallenge, navigate]);

  const isCreatureSelected = (id: string) => selectedCreatures.includes(id);

  // Get sprite URL for creature
  const getCreatureSprite = (creature: SerializedCreatureDocument): string => {
    // menuSprite is a gs:// URL, convert to public URL
    if (creature.sprites?.menuSprite) {
      const gsUrl = creature.sprites.menuSprite;
      // Convert gs://bucket/path to https://storage.googleapis.com/bucket/path
      if (gsUrl.startsWith('gs://')) {
        const path = gsUrl.replace('gs://', '');
        return `https://storage.googleapis.com/${path}`;
      }
      return gsUrl;
    }
    return ''; // Fallback - no sprite
  };

  // Get creature stats display
  const getCreatureStats = (creature: SerializedCreatureDocument) => {
    if (creature.primaryAttributes) {
      return creature.primaryAttributes;
    }
    // Fallback for creatures without primary attributes
    return { hp: 100, attack: 10, defense: 5, speed: 5 };
  };

  if (!dayData) {
    return (
      <PageAtmosphere page="challenge-day">
        <div className="challenge-day-page">
          <div className="cdp-loading">
            <div className="cdp-loading-spinner" />
            <p>Loading day data...</p>
          </div>
        </div>
      </PageAtmosphere>
    );
  }

  return (
    <PageAtmosphere page="challenge-day">
    <div className="challenge-day-page">
      <div className="day-page-content">
        {/* Back Link */}
        <Link to="/challenges" className="back-link">
          ← Back to Hub
        </Link>

        {/* Day Header */}
        <div className="day-header">
          <span className="day-number">Day {dayNum}</span>
          <h1 className="day-title">{dayData.name}</h1>
        </div>

        {/* Flavor Text */}
        <div className="flavor-box">
          <p>{dayData.flavor}</p>
        </div>

        {/* Main Content Grid */}
        <div className="day-content-grid">
          {/* Left: Enemy Info */}
          <div className="enemy-section">
            <h2>Enemies</h2>
            <div className="enemy-list">
              {(dayData.enemies || []).map((enemy, idx) => (
                <div key={idx} className={`enemy-card ${enemy.difficulty}`}>
                  <div className="enemy-header">
                    <span className="enemy-name">{enemy.name}</span>
                    <span className="enemy-difficulty">{enemy.difficulty}</span>
                  </div>
                  <div className="enemy-count">×{enemy.count}</div>
                </div>
              ))}
            </div>

            <div className="mode-info">
              <span className="mode-icon">{getModeIcon(dayData.mode)}</span>
              <span className="mode-label">Mode: {formatModeName(dayData.mode)}</span>
            </div>

            <div className="map-info">
              <span className="map-label">Map: {dayData.mapTemplate}</span>
            </div>

            {/* Boss Mechanics (if any) */}
            {dayData.bossMechanics && dayData.bossMechanics.length > 0 && (
              <div className="mechanics-section">
                <h3>Boss Mechanics</h3>
                <ul className="mechanics-list">
                  {dayData.bossMechanics.map((mechanic, idx) => (
                    <li key={idx}>{mechanic}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right: Creature Selection */}
          <div className="selection-section">
            <h2>Your Army (3-6 creatures)</h2>

            {/* Loading state */}
            {creaturesLoading && (
              <div className="creatures-loading">
                <p>Loading your creatures...</p>
              </div>
            )}

            {/* Error state */}
            {creaturesError && (
              <div className="creatures-error">
                <p>Error: {creaturesError}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
              </div>
            )}

            {/* No creatures */}
            {!creaturesLoading && !creaturesError && playerCreatures.length === 0 && (
              <div className="no-creatures">
                <p>You don't have any creatures yet!</p>
                <Link to="/create" className="create-link">Create your first creature</Link>
              </div>
            )}

            {/* Creature grid */}
            {!creaturesLoading && playerCreatures.length > 0 && (
              <div className="creature-grid">
                {playerCreatures.map((creature) => {
                  const stats = getCreatureStats(creature);
                  const spriteUrl = getCreatureSprite(creature);

                  return (
                    <div
                      key={creature.id}
                      className={`creature-select-card ${
                        isCreatureSelected(creature.id) ? 'selected' : ''
                      }`}
                      onClick={() => toggleCreature(creature.id)}
                    >
                      {spriteUrl ? (
                        <img
                          src={spriteUrl}
                          alt={creature.name}
                          className="creature-sprite"
                          onError={(e) => {
                            // Hide broken image
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="creature-placeholder">
                          {creature.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="creature-name">{creature.name}</div>
                      <div className="creature-stats">
                        <span title="HP">❤️{stats.hp}</span>
                        <span title="Attack">⚔️{stats.attack}</span>
                      </div>
                      {isCreatureSelected(creature.id) && (
                        <div className="selected-badge">✓</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="selection-info">
              <span>Selected: {selectedCreatures.length}/6</span>
              <span className={selectedCreatures.length < 3 ? 'warning' : ''}>
                {selectedCreatures.length < 3 ? 'Need at least 3' : 'Ready!'}
              </span>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message">
            {error}
            <button onClick={clearError}>Dismiss</button>
          </div>
        )}

        {/* Start Button */}
        <div className="start-section">
          <button
            className="enter-battle-button"
            onClick={handleStartBattle}
            disabled={selectedCreatures.length < 3 || isStartingChallenge || creaturesLoading}
          >
            {isStartingChallenge ? 'Starting...' : 'ENTER BATTLE'}
          </button>
        </div>

        {/* Previous Attempts */}
        {dayProgress && dayProgress.attempts > 0 && (
          <div className="attempts-info">
            <span>Previous attempts: {dayProgress.attempts}</span>
            {dayProgress.bestResult && (
              <span>Best: {'⭐'.repeat(dayProgress.bestResult.stars)}</span>
            )}
          </div>
        )}
      </div>
    </div>
    </PageAtmosphere>
  );
};
